import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import StaffListSummaryFilterSection from './components/ClockListSummaryFilterSection';
import { Form, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SetClockDialog from './components/SetClockDialog';
import { FormType } from '@/api/enum/formType.enum';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import {
	useDeleteClockMutation,
	useGetClockListQuery,
} from '@/api/app-setting/clockApiSlice';
import {
	ClickPageDTO,
	ClockPageFilter,
} from '@/api/DTO/app-setting/clock.interface';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import ReSetDefaultClockDialog from './components/ReSetDefaultClockDialog';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';

export interface clickListDataGrid {
	updatedAt: string;
}

export default function ClockListSummaryFilterSection() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const showModal = useModal();
	const [deleteClock] = useDeleteClockMutation();
	const [reSetOpen, setReSetOpen] = useState(false);
	const [type, setType] = useState<FormType>(FormType.edit);

	// useGetUser返回 userInfo 和 userRoles
	// 接收一个对象
	// 第一个入参shouldRefetch：是否再次调用获取用户信息api
	// 第二个入参roleObj：传入需要判断的权限返回权限对象boolean
	// roleObj的key自定义，值为需要判断的权限，返回自定义key和对应的boolean
	// const { userRoles } = useGetUser<useGetUserResultType>({
	// 	shouldRefetch: true,
	// 	roleObj: {
	// 		create: ['create', 'staffList'],
	// 	},
	// });

	const defaultPropertyFilter: ClockPageFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<ClockPageFilter> = defaultPropertyFilter;
	const methods = useForm<ClockPageFilter>({
		defaultValues,
	});
	const transformedSearchParams: ClockPageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetClockListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: ClickPageDTO[]) => {
		if (response == null) return;
		const newResponse: clickListDataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as ClockPageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'sort',
			headerName: t('appSetting.index'),
			flex: 5,
		},
		{
			field: 'fileName',
			headerName: t('appSetting.video'),
			flex: 21,
			renderCell: (params) => (
				<Link target='_blank' to={params.row.media?.url}>
					{params.row.fileName}
				</Link>
			),
		},
		{
			field: 'useCount',
			headerName: t('appSetting.userNum'),
			flex: 21,
		},
		{
			field: 'isDefault',
			headerName: t('appSetting.isPreinstall'),
			flex: 21,
			renderCell: (params: any) => {
				return params.row.isDefault == 1 ? t('common.yes') : t('common.no');
			},
		},
		{
			field: 'updatedAt',
			headerName: t('appSetting.uploadTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<SetClockDialog type={FormType.edit} item={params.row} />
			),
		},
		{
			field: 'delete',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<IconButton color='error' onClick={() => handleDelete(params.row)}>
					<DeleteOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleDelete = async (obj: ClickPageDTO) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('appSetting.deleteVideo')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('appSetting.isDeleteVideo')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteClock(obj.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.clockDeleteSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						if (obj.isDefault == 1) setReSetOpen(true);
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.clockList')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<Button
						variant='contained'
						endIcon={<MusicNoteOutlinedIcon />}
						onClick={() => {
							setReSetOpen(true);
							setType(FormType.create);
						}}
						style={{ alignItems: 'center' }}
					>
						{t('appSetting.setClock')}
					</Button>
					<SetClockDialog type={FormType.create} />
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
			<ReSetDefaultClockDialog
				reSetOpen={reSetOpen}
				setReSetOpen={setReSetOpen}
				type={type}
				setType={setType}
			/>
		</>
	);
}
